import type {
  PermissionActionKey,
  PermissionKey,
  PermissionPath,
  RequestBodyAccountLogin,
  ResponseAccountLogin,
  ResponseAccountProfile,
  ResponseRolePermission,
  RolePermissionMap,
} from "@/utils/types/auth";
import type { NavigationLink } from "@/utils/navigation";
import type { UserDetail } from "@/utils/types/user";

export const useAuthStore = defineStore(
  "auth-store",
  () => {
    const token = ref<ResponseAccountLogin>();
    const user = ref<UserDetail>();
    const type = ref<"store" | "staff">("store");
    const permissions = ref<RolePermissionMap>();

    const login = async (body: RequestBodyAccountLogin) => {
      try {
        const res = await useApi<ResponseAccountLogin>(
          "/v1/accounts/login",
          {
            method: "post",
            body: body,
            retry: 0,
          },
          {
            noAuth: true,
          },
        );

        token.value = res;
      } catch (err) {
        if (isApiAbortError(err)) throw err;

        token.value = undefined;
        throw err;
      }
    };

    const logout = async () => {
      if (token.value?.refresh !== undefined) {
        try {
          await useApi("/v1/accounts/logout", {
            method: "post",
            body: {
              refresh: token.value?.refresh,
            },
          });
        } catch (err) {
          console.error(err);
        } finally {
          token.value = undefined;
          user.value = undefined;
          permissions.value = undefined;
        }
      }
    };

    const refreshToken = async () => {
      try {
        const res = await useApi<ResponseAccountLogin>(
          "/v1/accounts/refresh_token",
          {
            method: "post",
            body: {
              refresh: token.value?.refresh,
            },
          },

          {
            noAuth: true,
          },
        );

        token.value = {
          ...token.value,
          ...res,
        };
      } catch (err) {
        if (isApiAbortError(err)) throw err;
        console.error(err);

        token.value = undefined;
        throw new Error("Something went wrong", { cause: err });
      }
    };

    const whoAmI = async () => {
      try {
        const res = await useApi<UserDetail>("/v1/accounts/whoami", {
          method: "get",
        });

        user.value = res;
        type.value = isStoreUser.value ? "store" : "staff";
      } catch (err) {
        if (isApiAbortError(err)) throw err;
        console.error(err);
        user.value = undefined;
        throw new Error("Something went wrong", { cause: err });
      }
    };

    const getProfile = async () => {
      return await useApi<ResponseAccountProfile>(`/v1/accounts/me/profile`, {
        method: "get",
      });
    };

    const getPermission = async () => {
      return await useApi<ResponseRolePermission>(
        `/v1/accounts/me/permissions`,
        {
          method: "get",
        },
      );
    };

    const hasPermissions = (paths: PermissionPath[]) => {
      if ((paths || [])?.length === 0) {
        return true;
      }

      return paths?.every((permission) => {
        const separatorIndex = permission.lastIndexOf(".");
        const permissionKey = permission.slice(
          0,
          separatorIndex,
        ) as PermissionKey;
        const action = permission.slice(
          separatorIndex + 1,
        ) as PermissionActionKey;

        return permissions.value?.[permissionKey]?.[action] === true;
      });
    };

    const hasNavigationRoleAccess = (
      link: Pick<NavigationLink, "includeRoles" | "excludesRoles">,
    ) => {
      const { includeRoles, excludesRoles } = link;

      if (includeRoles === undefined && excludesRoles === undefined) {
        return true;
      }

      if (user.value?.role === undefined) {
        return false;
      }

      return (
        (includeRoles === undefined ||
          includeRoles.includes(user.value.role)) &&
        (excludesRoles === undefined ||
          !excludesRoles.includes(user.value.role))
      );
    };

    const isSuperUser = computed(() => user.value?.role === "super_admin");
    const isSalesUser = computed(() => user.value?.role === "sales");
    const isSaleManagerUser = computed(
      () => user.value?.role === "sales_manager",
    );
    const isSalesAdminUser = computed(() => user.value?.role === "sales_admin");
    const isSalesDirectorUser = computed(
      () => user.value?.role === "sales_director",
    );
    const isCreditAdminUser = computed(
      () => user.value?.role === "credit_admin",
    );
    const isCreditDirectorUser = computed(
      () => user.value?.role === "credit_director",
    );
    const isStoreUser = computed(() => user.value?.role === "client");

    return {
      token,
      user,
      type,
      permissions,

      isSuperUser,
      isSalesUser,
      isSaleManagerUser,
      isSalesAdminUser,
      isSalesDirectorUser,
      isCreditAdminUser,
      isCreditDirectorUser,
      isStoreUser,

      login,
      logout,
      refreshToken,
      whoAmI,
      getProfile,
      getPermission,
      hasPermissions,
      hasNavigationRoleAccess,
    };
  },
  { persist: true },
);
